import axios from 'axios';

// 获取当前环境和部署类型
const isDevelopment = process.env.NODE_ENV === 'development';
const currentHost = window.location.hostname;

const getApiBaseUrl = () => {
  // 如果通过环境变量明确设置了API URL，则使用它
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // 如果是开发环境或本地访问
  if (isDevelopment || currentHost === 'localhost') {
    return 'http://localhost:5000/api';
  }

  // 生产环境：同源相对路径，自动适配任何域名/IP/协议（nginx 统一代理 /api 到后端）
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

console.log('当前环境:', process.env.NODE_ENV);
console.log('当前主机名:', currentHost);
console.log('API基础URL:', API_BASE_URL);

// 创建axios实例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 增加超时时间到300秒（5分钟），支持重写等长时间操作
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // 允许跨域请求携带凭证
});

// 为请求添加拦截器
api.interceptors.request.use(
  (config) => {
    // 从本地存储获取令牌
    const token = localStorage.getItem('authToken');
    
    // 如果存在令牌，添加到请求头
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 对批量生成API增加更长的超时时间
    if (config.url && (config.url.includes('/generate-batch') || config.url.includes('/batch-generate'))) {
      config.timeout = 1800000; // 批量生成API设置30分钟超时
      console.log('批量生成API请求，设置超时时间为30分钟');
    }
    
    // 添加请求日志（优化：对于大纲PATCH请求，清晰标注是单字段更新而非完整保存）
    const isOutlinePatch = config.url && config.url.includes('/outline') && config.data?.patch;
    const logData = {
      headers: {
        'content-type': config.headers['content-type'],
        'authorization': config.headers['authorization'] ? '存在' : '不存在'
      },
      dataKeys: config.data ? Object.keys(config.data) : [],
      timeout: config.timeout
    };
    // 只有完整保存大纲时才显示 subPlots 状态，PATCH 模式不显示（因为只更新单个字段）
    if (config.data?.subPlots !== undefined) {
      logData.subPlots = '存在,长度=' + config.data.subPlots.length;
    } else if (isOutlinePatch) {
      logData.subPlots = 'PATCH模式(单字段更新),不包含subPlots';
    }
    // 🔇 默认关闭请求日志；控制台执行 window.__API_DEBUG__ = true 可临时开启
    if (window.__API_DEBUG__) console.log(`[请求] ${config.method.toUpperCase()} ${config.url}`, logData);
    
    return config;
  },
  (error) => {
    console.error('请求拦截器错误:', error);
    return Promise.reject(error);
  }
);

// 为响应添加拦截器
api.interceptors.response.use(
  (response) => {
    // 成功请求的处理
    // 注意：过滤掉响应中的模型名称，避免在前端控制台暴露后端配置
    const sanitizeResponse = (data) => {
      if (!data || typeof data !== 'object') return data;
      const sanitized = { ...data };
      // 过滤 model 字段
      if (sanitized.model) sanitized.model = '***';
      if (sanitized.choices) {
        sanitized.choices = sanitized.choices.map(choice => {
          if (choice.delta?.model) {
            return { ...choice, delta: { ...choice.delta, model: undefined } };
          }
          return choice;
        });
      }
      return sanitized;
    };
    // 🔇 默认关闭响应日志；控制台执行 window.__API_DEBUG__ = true 可临时开启
    if (window.__API_DEBUG__) console.log(`[响应] ${response.config.url}`, {
      status: response.status,
      statusText: response.statusText,
      data: sanitizeResponse(response.data)
    });
    const url = response.config?.url || '';
    const method = (response.config?.method || '').toLowerCase();
    const isAssistAiProxy = url.includes('/assist/ai/proxy-ai');
    const isAssistRecommend =
      method === 'post' && url.includes('/assist/novels/') && url.includes('/characters/recommend');
    if (method === 'post' && (isAssistAiProxy || isAssistRecommend)) {
      try {
        window.dispatchEvent(new CustomEvent('assistAiQuotaRefresh'));
      } catch (_) {}
    }
    return response;
  },
  (error) => {
    // 处理响应错误
    let userMessage = '';
    let serverMessage = '';
    let statusCode = error.response ? error.response.status : null;
    const requestUrl = error.config?.url || '未知路径';
    
    // 对于充值接口的400/404错误，这些是业务逻辑错误，不应该记录为错误
    const isRechargeError = requestUrl.includes('/recharge/card-key') && 
                            (statusCode === 400 || statusCode === 404);
    
    if (isRechargeError) {
      // 业务错误，使用警告级别日志，不显示为错误
      console.warn(`[业务提示] ${requestUrl}`, {
        status: statusCode,
        message: error.response?.data?.message || error.message,
        response: error.response?.data
      });
    } else {
      // 真正的错误，记录为错误
      console.error(`[错误] ${requestUrl}`, {
      status: statusCode,
      message: error.message,
      response: error.response?.data
    });
    }
    
    if (!error.response) {
      userMessage = '网络错误，请检查您的网络连接';
      serverMessage = '网络请求失败，无响应';
      if (!isRechargeError) {
      console.error('网络错误，无响应:', error.message);
      }
    } else {
      // 根据状态码处理不同错误
      switch (statusCode) {
        case 400:
          userMessage = error.response.data?.message || '请求参数有误，请重试';
          serverMessage = '无效请求参数';
          break;
        case 401:
          userMessage = '登录已过期，请重新登录';
          serverMessage = '未授权访问';
          // 清除认证数据
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('sessionId');
          // 触发会话过期事件
          window.dispatchEvent(new CustomEvent('sessionExpired'));
          break;
        case 403:
          userMessage = '您没有权限执行此操作';
          serverMessage = '禁止访问';
          break;
        case 429:
          userMessage = error.response.data?.message || '今日免费次数已用完，请明天再来';
          serverMessage = '次数已用尽';
          break;
        case 404:
          // 处理API路径不存在的情况
          // 对于充值接口的404，已经在上面处理了，这里只处理其他404
          if (!isRechargeError) {
          console.error('API路径不存在:', error.config.url);
          }
          userMessage = '请求的资源不存在';
          serverMessage = '资源未找到';
          break;
        case 500:
          userMessage = '服务器内部错误，请稍后再试';
          serverMessage = '服务器内部错误';
          break;
        default:
          userMessage = '请求失败，请稍后再试';
          serverMessage = `HTTP错误 ${statusCode}`;
          break;
      }
    }
    
    // 添加用户友好消息到错误对象
    error.userMessage = userMessage;
    error.serverMessage = serverMessage;
    error.statusCode = statusCode;
    
    return Promise.reject(error);
  }
);

export default api;
export { getApiBaseUrl, API_BASE_URL }; 